/**
 * Route display UI component
 * Displays calculated route with building purchase order
 */

import { getProgress, saveProgress, updateProgress, clearProgress, updateSavedRoute, getSavedRouteById } from '../storage.js';
import { getImportedSaveGame } from '../save-game-importer.js';
import { getAchievementById } from '../utils/achievements.js';
import { formatNumber } from '../utils/format.js';
import { exportRoute, detectRouteType } from './route-export.js';
import { getRouteUpdateState } from '../utils/route-update.js';

export class RouteDisplay {
  constructor(containerId, onSaveRoute = null, saveGameImportDialog = null) {
    this.container = document.getElementById(containerId);
    this.currentRoute = null;
    this.progress = null;
    this.currentCategory = null;
    this.currentVersionId = null;
    this.onSaveRoute = onSaveRoute; // Callback when save route is clicked
    this.tooltipElement = null; // Shared tooltip element
    this.buildingOrder = null; // Building order from version data
    this.currentSavedRoute = null; // Store full saved route object when displaying saved route
    this.isUpdating = false; // Track if route update is in progress
    this.saveGameImportDialog = saveGameImportDialog; // Reference to existing import dialog
    this.pendingRouteUpdate = false; // Flag to track if we're waiting for import for route update
  }

  /**
   * Set the current category and version for saving routes
   * @param {Object} category - Current category
   * @param {string} versionId - Current version ID
   */
  async setCategoryAndVersion(category, versionId) {
    this.currentCategory = category;
    this.currentVersionId = versionId;
    // Load building order from version data
    await this.loadBuildingOrder(versionId);
  }

  /**
   * Load building order from version data
   * @param {string} versionId - Version ID
   */
  async loadBuildingOrder(versionId) {
    if (!versionId) {
      versionId = this.currentVersionId || 'v2052';
    }
    
    try {
      const { loadVersionById } = await import('../utils/version-loader.js');
      const version = await loadVersionById(versionId);
      this.buildingOrder = version.buildingNames || [];
    } catch (error) {
      console.warn(`Failed to load building order for ${versionId}, using default`, error);
      // Fallback to a default order if loading fails
      this.buildingOrder = ['Cursor', 'Grandma', 'Farm', 'Mine', 'Factory', 'Bank', 'Temple', 'Wizard tower', 'Shipment', 'Alchemy lab', 'Portal', 'Time machine', 'Antimatter condenser', 'Prism', 'Chancemaker', 'Fractal engine', 'Javascript console', 'Idleverse', 'Cortex baker', 'You'];
    }
  }

  /**
   * Sort buildings by game order
   * @param {Array} buildingEntries - Array of [buildingName, count] tuples
   * @returns {Array} Sorted array
   */
  sortBuildingsByOrder(buildingEntries) {
    if (!this.buildingOrder || this.buildingOrder.length === 0) {
      // Fallback to alphabetical if no order available
      return buildingEntries.sort(([a], [b]) => a.localeCompare(b));
    }
    
    // Create a map of building name to index for fast lookup
    const orderMap = new Map();
    this.buildingOrder.forEach((name, index) => {
      orderMap.set(name, index);
    });
    
    return buildingEntries.sort(([a], [b]) => {
      const indexA = orderMap.get(a) ?? Infinity;
      const indexB = orderMap.get(b) ?? Infinity;
      return indexA - indexB;
    });
  }

  /**
   * Calculate derived values for a route step
   * Calculates cookiesPerSecond, timeElapsed, and totalCookies from stored values
   * @param {Object} step - Route step object
   * @param {Object} previousStep - Previous step (with calculated values) or null for first step
   * @returns {Object} Step with calculated values added
   */
  calculateStepValues(step, previousStep = null) {
    const calculated = { ...step };
    
    // Backward compatibility: if step already has calculated values, use them
    if (step.cookiesPerSecond !== undefined && step.timeElapsed !== undefined && step.totalCookies !== undefined) {
      // Old format - values already calculated, just return as-is
      return calculated;
    }
    
    if (previousStep === null) {
      // First step - use initial values if available, otherwise defaults
      calculated.cookiesPerSecond = step.initialCookiesPerSecond || step.cookiesPerSecond || 0;
      calculated.timeElapsed = step.initialTimeElapsed || step.timeElapsed || 0;
      calculated.totalCookies = step.initialTotalCookies || step.totalCookies || 0;
    } else {
      // Calculate from previous step
      calculated.cookiesPerSecond = previousStep.cookiesPerSecond + (step.cpsIncrease || 0);
      calculated.timeElapsed = previousStep.timeElapsed + (step.timeSinceLastStep || 0);
      
      // Calculate totalCookies: previous total - cost + cookies generated during wait time
      const cookiesAfterPurchase = previousStep.totalCookies - previousStep.cookiesRequired;
      const cookiesGenerated = previousStep.cookiesPerSecond * (step.timeSinceLastStep || 0);
      calculated.totalCookies = cookiesAfterPurchase + cookiesGenerated;
    }
    
    return calculated;
  }

  /**
   * Display a route (either calculated or saved)
   * @param {Object} route - Route object (calculated) or SavedRoute object
   * @param {boolean} isSavedRoute - Whether this is a saved route
   */
  async displayRoute(route, isSavedRoute = false) {
    if (!route) {
      this.currentRoute = null;
      this.progress = null;
      await this.render();
      return;
    }

    this.currentRoute = route;
    this.isSavedRoute = isSavedRoute;

    // For saved routes, use routeData and savedRouteId
    let versionId = null;
    if (isSavedRoute) {
      // Store full saved route object for update functionality
      this.currentSavedRoute = route;
      // Convert saved route to display format
      this.currentRoute = {
        id: route.id, // Use saved route ID for progress tracking
        buildings: route.routeData.buildings || [],
        completionTime: route.routeData.completionTime || 0,
        algorithm: route.routeData.algorithm || 'GPL',
        lookahead: route.routeData.lookahead || 1,
        startingBuildings: route.routeData.startingBuildings || {}
      };
      versionId = route.versionId;
    } else {
      this.currentSavedRoute = null;
      versionId = route.versionId || this.currentVersionId;
    }

    // Load building order for the route's version
    if (versionId) {
      await this.loadBuildingOrder(versionId);
    } else if (this.currentVersionId) {
      await this.loadBuildingOrder(this.currentVersionId);
    }

    // Get progress using route ID (works for both calculated and saved routes)
    this.progress = getProgress(route.id);
    if (!this.progress) {
      // Create and save new progress if it doesn't exist
      this.progress = {
        routeId: route.id,
        completedBuildings: [],
        lastUpdated: Date.now()
      };
      saveProgress(this.progress);
    }
    await this.render();
  }

  /**
   * Render the route display
   */
  async render() {
    if (!this.container || !this.currentRoute) {
      this.container.innerHTML = '<p class="empty-state">No route selected. Select a category and calculate a route.</p>';
      return;
    }

    // Save scroll position before re-rendering
    const routeList = this.container.querySelector('.route-list');
    const savedScrollTop = routeList ? routeList.scrollTop : 0;

    const { buildings, completionTime, algorithm } = this.currentRoute;
    const completedCount = this.progress.completedBuildings.length;
    const totalCount = buildings.length;
    const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

    // Get route summary HTML (async)
    const routeSummaryHtml = await this.renderRouteSummary();

    this.container.innerHTML = `
      <div class="route-header">
        <div class="route-header-top">
          <h2>Building Purchase Route${this.isSavedRoute ? ' (Saved)' : ''}</h2>
          <div class="route-header-actions">
            ${completedCount > 0 ? `<button id="reset-progress-btn" class="btn-secondary" aria-label="Reset all progress">Reset</button>` : ''}
            ${this.onSaveRoute && !this.isSavedRoute ? `<button id="save-route-btn" class="btn-primary" aria-label="Save this route">Save Route</button>` : ''}
            ${this.isSavedRoute && !this.isUpdating ? `<button id="update-route-btn" class="btn-primary" aria-label="Update route with imported save game data">Update Route</button>` : ''}
            ${this.isUpdating ? `<button id="cancel-update-btn" class="btn-secondary" aria-label="Cancel route update">Cancel</button>` : ''}
            <button id="export-route-btn" class="btn-secondary" aria-label="Export this route">Export</button>
          </div>
        </div>
        <div class="route-meta">
          <span>Algorithm: ${algorithm}</span>
          <span>Completion Time: ${this.formatTime(completionTime)}</span>
          <span>Total Steps: ${totalCount}</span>
          ${this.currentRoute.usedImportedData ? '<span class="imported-data-indicator" title="This route was calculated using imported save game data">📥 Using Imported Data</span>' : ''}
          ${this.currentRoute.achievementIds && this.currentRoute.achievementIds.length > 0 ? `
            <span class="achievement-route-indicator" title="Achievement-based route">
              🏆 ${this.currentRoute.achievementIds.length} Achievement${this.currentRoute.achievementIds.length !== 1 ? 's' : ''}
            </span>
            ${this.currentRoute.achievementUnlocks && this.currentRoute.achievementUnlocks.length > 0 ? `
              <span class="achievement-completion-summary" title="Achievements unlocked during route">
                ✓ ${this.currentRoute.achievementUnlocks.reduce((sum, unlock) => sum + unlock.achievementIds.length, 0)} Unlocked
              </span>
            ` : ''}
          ` : ''}
        </div>
        <div class="progress-bar-container">
          <div class="progress-bar" role="progressbar" 
               aria-valuenow="${progressPercent}" 
               aria-valuemin="0" 
               aria-valuemax="100"
               style="width: ${progressPercent}%">
          </div>
          <span class="progress-text">${completedCount} / ${totalCount} completed</span>
        </div>
      </div>
      ${routeSummaryHtml}
      <div class="route-list" role="list">
        ${(() => {
          // Pre-calculate all step values efficiently (only calculate each step once)
          const calculatedSteps = [];
          for (let i = 0; i < buildings.length; i++) {
            const previousStep = i > 0 ? calculatedSteps[i - 1] : null;
            calculatedSteps.push(this.calculateStepValues(buildings[i], previousStep));
          }
          return calculatedSteps.map((calculatedStep, index) => {
            const isCompleted = this.progress.completedBuildings.includes(calculatedStep.order);
            
            // Handle different step types
            let stepContentHtml = '';
            if (calculatedStep.type === 'sugarLumpHarvest') {
              // Sugar Lump harvest event
              stepContentHtml = `
              <div class="step-content sugar-lump-harvest">
                <div class="step-info">
                  <span class="step-icon">🍬</span>
                  <span class="step-building">Sugar Lump Harvested</span>
                  <span class="step-separator">•</span>
                  <span class="step-detail">Harvest #${calculatedStep.harvestNumber}</span>
                  <span class="step-separator">•</span>
                  <span class="step-detail">Available: ${calculatedStep.availableSugarLumps}</span>
                  <span class="step-separator">•</span>
                  <span class="step-detail">Time: ${this.formatTime(calculatedStep.timeElapsed)}</span>
                </div>
              </div>
              `;
            } else if (calculatedStep.type === 'buildingLevelUpgrade') {
              // Building level upgrade event
              stepContentHtml = `
              <div class="step-content building-level-upgrade">
                <div class="step-info">
                  <span class="step-icon">⭐</span>
                  <span class="step-building">${this.escapeHtml(calculatedStep.buildingName)} Level ${calculatedStep.level}</span>
                  <span class="step-separator">•</span>
                  <span class="step-detail">Cost: ${calculatedStep.cost} Sugar Lump${calculatedStep.cost !== 1 ? 's' : ''}</span>
                  <span class="step-separator">•</span>
                  <span class="step-detail">Available: ${calculatedStep.availableSugarLumps}</span>
                  <span class="step-separator">•</span>
                  <span class="step-detail">Time: ${this.formatTime(calculatedStep.timeElapsed)}</span>
                </div>
              </div>
              `;
            } else {
              // Regular building/upgrade step
              stepContentHtml = `
              <div class="step-content">
                <div class="step-info">
                  <span class="step-building">${this.escapeHtml(calculatedStep.buildingName)}${calculatedStep.buildingCount !== null && calculatedStep.buildingCount !== undefined ? ` [${calculatedStep.buildingCount}]` : ''}</span>
                  <span class="step-separator">•</span>
                  <span class="step-detail">Cookies: ${formatNumber(calculatedStep.cookiesRequired)}</span>
                  <span class="step-separator">•</span>
                  <span class="step-detail">CpS: ${formatNumber(calculatedStep.cookiesPerSecond)}${calculatedStep.cpsIncrease !== undefined && calculatedStep.cpsIncrease > 0 ? ` (+${formatNumber(calculatedStep.cpsIncrease)})` : ''}</span>
                  <span class="step-separator">•</span>
                  <span class="step-detail">Time: ${this.formatTime(calculatedStep.timeElapsed)}</span>
                  ${calculatedStep.timeSinceLastStep !== undefined ? `
                  <span class="step-separator">•</span>
                  <span class="step-detail">Since Last: ${this.formatTime(calculatedStep.timeSinceLastStep)}</span>
                  ` : ''}
                </div>
                ${calculatedStep.achievementUnlocks && calculatedStep.achievementUnlocks.length > 0 ? `
                <div class="step-achievement-unlocks">
                  <span class="achievement-unlock-icon">🏆</span>
                  <span class="achievement-unlock-text">
                    Achievement${calculatedStep.achievementUnlocks.length !== 1 ? 's' : ''} unlocked: 
                    ${calculatedStep.achievementUnlocks.map(id => {
                      const achievement = getAchievementById(id);
                      return achievement ? achievement.name : `Achievement ${id}`;
                    }).join(', ')}
                  </span>
                </div>
                ` : ''}
              </div>
              `;
            }
            
            // Build the full step HTML
            return `
            <div class="route-step ${isCompleted ? 'completed' : ''}" 
                 role="listitem"
                 data-step-order="${calculatedStep.order}">
              <label class="step-checkbox">
                <input 
                  type="checkbox" 
                  ${isCompleted ? 'checked' : ''}
                  aria-label="Mark step ${calculatedStep.order} as completed"
                  data-step-order="${calculatedStep.order}"
                >
                <span class="step-number">${calculatedStep.order}</span>
              </label>
              ${stepContentHtml}
              ${calculatedStep.type !== 'sugarLumpHarvest' && calculatedStep.type !== 'buildingLevelUpgrade' ? `
              <button 
                class="step-check-all-btn" 
                data-step-order="${calculatedStep.order}"
                aria-label="Check all previous steps up to step ${calculatedStep.order}"
                title="Check all previous steps">
                ✓ All
              </button>
              <button 
                class="step-info-icon" 
                data-step-order="${calculatedStep.order}"
                aria-label="Show summary up to step ${calculatedStep.order}"
                title="Show summary">
                ℹ
              </button>
              ` : ''}
            </div>
          `;
          }).join('');
        })()}
      </div>
    `;

    // Attach checkbox event listeners
    this.container.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const stepOrder = parseInt(e.target.dataset.stepOrder);
        this.toggleStep(stepOrder, e.target.checked);
      });
    });

    // Attach "check all previous" button listeners
    this.container.querySelectorAll('.step-check-all-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        // Use currentTarget to ensure we get the button, not a child element
        const stepOrder = parseInt(e.currentTarget.dataset.stepOrder);
        if (!isNaN(stepOrder)) {
          this.checkAllPreviousSteps(stepOrder);
        }
      });
    });

    // Attach reset progress button listener
    const resetBtn = this.container.querySelector('#reset-progress-btn');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        if (confirm('Are you sure you want to reset all progress? This will uncheck all steps.')) {
          this.resetAllProgress();
        }
      });
    }

    // Attach save route button listener
    const saveBtn = this.container.querySelector('#save-route-btn');
    if (saveBtn && this.onSaveRoute) {
      saveBtn.addEventListener('click', () => {
        if (this.onSaveRoute) {
          this.onSaveRoute(this.currentRoute, this.currentCategory, this.currentVersionId);
        }
      });
    }

    // Attach update route button listener
    const updateBtn = this.container.querySelector('#update-route-btn');
    if (updateBtn && this.isSavedRoute && this.currentSavedRoute) {
      updateBtn.addEventListener('click', () => {
        this.handleUpdateRoute();
      });
    }

    // Attach cancel update button listener
    const cancelUpdateBtn = this.container.querySelector('#cancel-update-btn');
    if (cancelUpdateBtn) {
      cancelUpdateBtn.addEventListener('click', () => {
        this.handleCancelUpdate();
      });
    }

    // Attach export route button listener
    const exportBtn = this.container.querySelector('#export-route-btn');
    if (exportBtn && this.currentRoute) {
      exportBtn.addEventListener('click', () => {
        try {
          // Determine route type and name
          let routeType;
          let routeName;

          if (this.currentRoute.achievementIds && this.currentRoute.achievementIds.length > 0) {
            routeType = 'achievementRoute';
            routeName = `Achievement Route (${this.currentRoute.achievementIds.join(', ')})`;
          } else {
            routeType = 'calculatedRoute';
            routeName = this.currentCategory?.name || 'Calculated Route';
          }

          // Prepare route data for export
          const routeData = {
            categoryId: this.currentCategory?.id,
            categoryName: this.currentCategory?.name,
            versionId: this.currentVersionId || 'v2052',
            buildings: this.currentRoute.buildings,
            algorithm: this.currentRoute.algorithm,
            lookahead: this.currentRoute.lookahead,
            completionTime: this.currentRoute.completionTime,
            startingBuildings: this.currentRoute.startingBuildings || {},
            calculatedAt: Date.now(),
            achievementIds: this.currentRoute.achievementIds
          };

          exportRoute(routeData, routeType, routeName);
        } catch (error) {
          console.error('Failed to export route:', error);
          alert(`Failed to export route: ${error.message}`);
        }
      });
    }

    // Attach tooltip event listeners
    this.attachTooltipListeners();
    
    // Attach building upgrades tooltip listeners
    this.attachBuildingUpgradesTooltipListeners();

    // Attach route summary toggle listener
    const summaryToggle = this.container.querySelector('.route-summary-toggle');
    if (summaryToggle) {
      summaryToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const content = this.container.querySelector('.route-summary-content');
        if (content) {
          const isCurrentlyExpanded = summaryToggle.getAttribute('aria-expanded') === 'true';
          const willBeExpanded = !isCurrentlyExpanded;
          
          // Update collapsed class based on new state
          if (willBeExpanded) {
            content.classList.remove('collapsed');
          } else {
            content.classList.add('collapsed');
          }
          
          // Update aria-expanded attribute
          summaryToggle.setAttribute('aria-expanded', willBeExpanded ? 'true' : 'false');
          
          // Update icon to reflect new state
          const icon = summaryToggle.querySelector('.collapse-icon');
          if (icon) {
            icon.textContent = willBeExpanded ? '▼' : '▶';
          }
        }
      });
    }

    // Restore scroll position after rendering
    if (savedScrollTop > 0) {
      const newRouteList = this.container.querySelector('.route-list');
      if (newRouteList) {
        // Use requestAnimationFrame to ensure DOM is fully updated
        requestAnimationFrame(() => {
          newRouteList.scrollTop = savedScrollTop;
        });
      }
    }
  }

  /**
   * Reset all progress - uncheck all steps
   */
  async resetAllProgress() {
    if (!this.currentRoute) return;
    
    // Clear progress from storage
    clearProgress(this.currentRoute.id);
    
    // Reset progress object
    this.progress = {
      routeId: this.currentRoute.id,
      completedBuildings: [],
      lastUpdated: Date.now()
    };
    
    // Save the empty progress
    saveProgress(this.progress);
    
    // Re-render to update the UI
    await this.render();
  }

  /**
   * Check all previous steps up to and including the given step
   * @param {number} stepOrder - The step order to check up to
   */
  async checkAllPreviousSteps(stepOrder) {
    if (!this.currentRoute) return;
    if (!stepOrder || stepOrder < 1) return;
    
    // Get fresh progress from storage to ensure we have the latest state
    const currentProgress = getProgress(this.currentRoute.id);
    
    // Ensure progress exists
    if (!currentProgress) {
      this.progress = {
        routeId: this.currentRoute.id,
        completedBuildings: [],
        lastUpdated: Date.now()
      };
      saveProgress(this.progress);
    } else {
      this.progress = currentProgress;
    }

    // Build a new array containing ONLY steps from 1 to stepOrder
    // This ensures we never include steps beyond the clicked step
    const finalCompletedBuildings = [];
    for (let i = 1; i <= stepOrder; i++) {
      // Include step i if it was already completed OR if we're checking it now
      if (this.progress.completedBuildings.includes(i)) {
        finalCompletedBuildings.push(i);
      } else {
        // This step wasn't completed, so we're checking it now
        finalCompletedBuildings.push(i);
      }
    }
    
    // Sort to ensure proper order
    finalCompletedBuildings.sort((a, b) => a - b);
    
    // Update progress with only steps up to stepOrder
    this.progress.completedBuildings = finalCompletedBuildings;
    this.progress.lastUpdated = Date.now();
    this.progress.routeId = this.currentRoute.id;

    // Update progress in storage
    try {
      updateProgress(this.currentRoute.id, this.progress.completedBuildings);
    } catch (error) {
      if (error.message && error.message.includes('not found')) {
        saveProgress(this.progress);
      } else {
        console.error('Error updating progress:', error);
        saveProgress(this.progress);
      }
    }
    
    await this.render(); // Re-render to update progress bar and checkboxes
  }

  /**
   * Toggle a step's completion status
   */
  async toggleStep(stepOrder, completed) {
    if (!this.currentRoute) return;
    
    // Ensure progress exists
    if (!this.progress) {
      // Create progress if it doesn't exist
      this.progress = {
        routeId: this.currentRoute.id,
        completedBuildings: [],
        lastUpdated: Date.now()
      };
      saveProgress(this.progress);
    }

    if (completed) {
      if (!this.progress.completedBuildings.includes(stepOrder)) {
        this.progress.completedBuildings.push(stepOrder);
        this.progress.completedBuildings.sort((a, b) => a - b);
      }
    } else {
      this.progress.completedBuildings = this.progress.completedBuildings.filter(
        order => order !== stepOrder
      );
    }

    this.progress.lastUpdated = Date.now();
    
    // Ensure progress has the correct routeId
    this.progress.routeId = this.currentRoute.id;
    
    // Update progress - use try/catch in case progress doesn't exist in storage
    try {
      updateProgress(this.currentRoute.id, this.progress.completedBuildings);
    } catch (error) {
      // If progress doesn't exist in storage, save it directly
      if (error.message && error.message.includes('not found')) {
        // Progress doesn't exist in storage, save it directly
        saveProgress(this.progress);
      } else {
        console.error('Error updating progress:', error);
        // Still try to save directly as fallback
        saveProgress(this.progress);
      }
    }
    
    await this.render(); // Re-render to update progress bar
  }

  /**
   * Show loading state
   * @param {number} steps - Optional number of steps calculated so far
   * @param {string} message - Optional custom loading message
   */
  showLoading(steps = 0, message = null) {
    if (!this.container) return;
    const stepsText = steps > 0 ? ` (${steps} steps calculated)` : '';
    const loadingMessage = message || `Calculating optimal route...${stepsText}`;
    this.container.innerHTML = `
      <div class="loading-state">
        <div class="spinner"></div>
        <p>${loadingMessage}</p>
      </div>
    `;
  }

  /**
   * Update loading state with progress
   * @param {number} steps - Number of steps calculated so far
   */
  updateLoadingProgress(steps) {
    if (!this.container) return;
    
    // Directly update the text - requestAnimationFrame might not help if calculation is blocking
    const loadingState = this.container.querySelector('.loading-state');
    if (loadingState) {
      const textElement = loadingState.querySelector('p');
      if (textElement) {
        textElement.textContent = `Calculating optimal route... (${steps} steps calculated)`;
      } else {
        // Fallback: recreate the loading state if structure is wrong
        const stepsText = steps > 0 ? ` (${steps} steps calculated)` : '';
        this.container.innerHTML = `
          <div class="loading-state">
            <div class="spinner"></div>
            <p>Calculating optimal route...${stepsText}</p>
          </div>
        `;
      }
    } else {
      // If loading state doesn't exist, recreate it
      const stepsText = steps > 0 ? ` (${steps} steps calculated)` : '';
      this.container.innerHTML = `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Calculating optimal route...${stepsText}</p>
        </div>
      `;
    }
  }

  /**
   * Show error state
   */
  showError(message) {
    if (!this.container) return;
    this.container.innerHTML = `
      <div class="error-state">
        <p class="error-message">${this.escapeHtml(message)}</p>
        <button class="retry-button" onclick="location.reload()">Retry</button>
      </div>
    `;
  }

  /**
   * Show success message
   * @param {string} message - Success message to display
   */
  showSuccess(message) {
    if (!this.container) return;
    const successElement = document.createElement('div');
    successElement.className = 'success-message';
    successElement.textContent = message;
    successElement.style.cssText = 'background-color: #4caf50; color: white; padding: 12px; border-radius: 4px; margin: 10px 0;';
    
    // Insert at the top of the container
    const firstChild = this.container.firstChild;
    if (firstChild) {
      this.container.insertBefore(successElement, firstChild);
    } else {
      this.container.appendChild(successElement);
    }
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (successElement.parentNode) {
        successElement.remove();
      }
    }, 3000);
  }

  /**
   * Handle route update button click - opens import dialog
   */
  handleUpdateRoute() {
    if (!this.isSavedRoute || !this.currentSavedRoute) {
      return;
    }

    // Check if update is already in progress
    const updateState = getRouteUpdateState(this.currentSavedRoute.id);
    if (updateState && updateState.isUpdating) {
      return; // Already updating
    }

    // Set flag to indicate we're waiting for import for route update
    this.pendingRouteUpdate = true;

    // Show the save game import dialog
    if (this.saveGameImportDialog) {
      // Store original callback to restore later
      const originalOnImport = this.saveGameImportDialog.onImport;
      
      // Temporarily override the onImport callback to handle route update
      // Skip the normal import flow (which shows route creation dialog) when updating a route
      this.saveGameImportDialog.onImport = (importedSaveGame) => {
        // Skip original callback (which shows route creation dialog) when updating route
        // Only handle route update
        if (this.pendingRouteUpdate) {
          this.pendingRouteUpdate = false;
          // Restore original callback
          this.saveGameImportDialog.onImport = originalOnImport;
          // Trigger route update directly without showing route creation dialog
          this.handleRouteUpdateAfterImport(importedSaveGame);
        } else {
          // If not updating route, use normal flow
          if (originalOnImport) {
            originalOnImport(importedSaveGame);
          }
        }
      };
      
      // Also handle dialog cancellation to clean up
      const originalHide = this.saveGameImportDialog.hide.bind(this.saveGameImportDialog);
      this.saveGameImportDialog.hide = () => {
        // Clean up if dialog is closed without importing
        if (this.pendingRouteUpdate) {
          this.pendingRouteUpdate = false;
          this.saveGameImportDialog.onImport = originalOnImport;
        }
        originalHide();
        // Restore original hide method
        this.saveGameImportDialog.hide = originalHide;
      };
      
      this.saveGameImportDialog.show();
    } else {
      this.showError('Import dialog not available. Please refresh the page.');
      this.pendingRouteUpdate = false;
    }
  }

  /**
   * Handle route update after save game import
   * @param {Object} importedSaveGame - The imported save game data
   */
  async handleRouteUpdateAfterImport(importedSaveGame) {
    if (!this.isSavedRoute || !this.currentSavedRoute) {
      return;
    }

    // Set updating flag
    this.isUpdating = true;
    await this.render(); // Re-render to show cancel button and hide update button

    // Show loading state with update message
    this.showLoading(0, 'Updating route with imported save game data...');

    try {
      // Update route
      const result = await updateSavedRoute(this.currentSavedRoute.id, importedSaveGame, {
        onProgress: (progress) => {
          this.updateLoadingProgress(progress.moves || 0);
        },
        preserveProgress: true
      });

      if (result.success) {
        // Reload the updated route
        const updatedRoute = getSavedRouteById(this.currentSavedRoute.id);
        if (updatedRoute) {
          await this.displayRoute(updatedRoute, true);
          const preservedCount = result.preservedProgress?.length || 0;
          const successMessage = preservedCount > 0 
            ? `Route updated successfully! Preserved ${preservedCount} completed step${preservedCount !== 1 ? 's' : ''}.`
            : 'Route updated successfully!';
          this.showSuccess(successMessage);
        } else {
          this.showError('Route updated but could not be reloaded. Please refresh the page.');
        }
      } else {
        // Show comprehensive error message based on error code
        const errorCode = result.error?.code || 'UNKNOWN_ERROR';
        let errorMessage = result.error?.message || 'Unknown error occurred during route update';
        
        // Provide user-friendly error messages
        switch (errorCode) {
          case 'VALIDATION_ERROR':
            errorMessage = `Validation failed: ${errorMessage}. Please check your imported save game data.`;
            break;
          case 'VERSION_MISMATCH':
            errorMessage = `Version mismatch: ${errorMessage}. The imported save game version may not be compatible with this route.`;
            break;
          case 'STORAGE_QUOTA_EXCEEDED':
            errorMessage = 'Storage quota exceeded. Please delete some old saved routes before updating.';
            break;
          case 'DATA_CORRUPTION':
            errorMessage = 'Corrupted route data detected. Please try reloading the route.';
            break;
          case 'CATEGORY_NOT_FOUND':
            errorMessage = `Category not found: ${errorMessage}. The route's category may have been deleted.`;
            break;
          case 'ROUTE_NOT_FOUND':
            errorMessage = 'Route not found. It may have been deleted.';
            break;
          case 'CALCULATION_ERROR':
            errorMessage = `Calculation failed: ${errorMessage}. The route has been preserved unchanged.`;
            break;
          default:
            errorMessage = `Failed to update route: ${errorMessage}`;
        }
        
        this.showError(errorMessage);
        // Reload original route to ensure it's still displayed
        await this.displayRoute(this.currentSavedRoute, true);
      }
    } catch (error) {
      console.error('Error updating route:', error);
      this.showError(`Failed to update route: ${error.message}`);
      // Reload original route to ensure it's still displayed
      await this.displayRoute(this.currentSavedRoute, true);
    } finally {
      this.isUpdating = false;
      await this.render(); // Re-render to restore normal state
    }
  }

  /**
   * Handle cancel update button click
   */
  async handleCancelUpdate() {
    if (!this.isSavedRoute || !this.currentSavedRoute) {
      return;
    }

    // Import cancelRouteUpdate
    const { cancelRouteUpdate } = await import('../utils/route-update.js');
    
    // Cancel the update
    const cancelled = cancelRouteUpdate(this.currentSavedRoute.id);
    
    if (cancelled) {
      this.isUpdating = false;
      await this.render(); // Re-render to restore normal state
      this.showSuccess('Route update cancelled.');
    }
  }

  /**
   * Format time in seconds to readable format
   * Includes years, days, hours, minutes, and seconds as appropriate
   * Handles very long durations (hundreds/thousands of days)
   */
  formatTime(seconds) {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    
    if (seconds < 3600) {
      return `${(seconds / 60).toFixed(1)}m`;
    }
    
    if (seconds < 86400) {
      // Less than 1 day, show hours
      return `${(seconds / 3600).toFixed(2)}h`;
    }
    
    // 1 day or more
    const days = Math.floor(seconds / 86400);
    const remainingSeconds = seconds % 86400;
    const hours = remainingSeconds / 3600;
    
    // Check if we need to show years (365 days = 1 year)
    if (days >= 365) {
      const years = Math.floor(days / 365);
      const remainingDays = days % 365;
      
      if (remainingDays === 0 && hours < 0.01) {
        // Exactly years, no remaining days/hours
        return `${years}y`;
      }
      
      if (remainingDays === 0) {
        // Years with hours only
        return `${years}y ${hours.toFixed(2)}h`;
      }
      
      if (hours < 0.01) {
        // Years and days, no hours
        return `${years}y ${remainingDays}d`;
      }
      
      // Years, days, and hours
      return `${years}y ${remainingDays}d ${hours.toFixed(2)}h`;
    }
    
    // Less than 1 year, show days and hours
    if (hours < 0.01) {
      // Less than 0.01 hours remaining, just show days
      return `${days}d`;
    }
    
    // Show days and hours
    return `${days}d ${hours.toFixed(2)}h`;
  }

  /**
   * Attach tooltip event listeners to info icons
   */
  attachTooltipListeners() {
    const infoIcons = this.container.querySelectorAll('.step-info-icon');

    // Create tooltip element if it doesn't exist
    if (!this.tooltipElement) {
      this.tooltipElement = document.createElement('div');
      this.tooltipElement.className = 'route-step-tooltip';
      this.tooltipElement.style.display = 'none';
      document.body.appendChild(this.tooltipElement);
    }

    infoIcons.forEach(iconElement => {
      const stepOrder = parseInt(iconElement.getAttribute('data-step-order'));
      if (isNaN(stepOrder)) return;

      const showTooltip = (e) => {
        if (!this.tooltipElement || !this.currentRoute) return;
        
        // Calculate summary for this step
        const summary = this.calculateSummaryUpToStep(stepOrder);
        const tooltipContent = this.formatSummaryTooltip(summary);
        this.tooltipElement.innerHTML = tooltipContent;
        this.tooltipElement.style.display = 'block';
        
        // Force reflow to get accurate dimensions
        this.tooltipElement.offsetHeight;
        
        // Position tooltip at mouse location
        const updateTooltipPosition = (mouseEvent) => {
          if (!this.tooltipElement) return;
          
          const tooltipRect = this.tooltipElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
          
          // Get mouse coordinates
          let mouseX = mouseEvent.clientX + scrollLeft;
          let mouseY = mouseEvent.clientY + scrollTop;
          
          // Offset tooltip slightly from cursor
          const offsetX = 15;
          const offsetY = 15;
          
          let left = mouseX + offsetX;
          let top = mouseY + offsetY;
          
          // Adjust if tooltip would go off screen
          if (left + tooltipRect.width > window.innerWidth + scrollLeft - 10) {
            left = mouseX - tooltipRect.width - offsetX;
          }
          if (top + tooltipRect.height > window.innerHeight + scrollTop - 10) {
            top = mouseY - tooltipRect.height - offsetY;
          }
          if (left < scrollLeft + 10) {
            left = scrollLeft + 10;
          }
          if (top < scrollTop + 10) {
            top = scrollTop + 10;
          }
          
          this.tooltipElement.style.top = `${top}px`;
          this.tooltipElement.style.left = `${left}px`;
        };
        
        // Initial position
        updateTooltipPosition(e);
        
        // Update position as mouse moves
        const mouseMoveHandler = (moveEvent) => {
          updateTooltipPosition(moveEvent);
        };
        
        iconElement.addEventListener('mousemove', mouseMoveHandler);
        
        // Store handler for cleanup
        iconElement._tooltipMouseMoveHandler = mouseMoveHandler;
      };

      const hideTooltip = () => {
        if (this.tooltipElement) {
          this.tooltipElement.style.display = 'none';
        }
        // Remove mousemove handler
        if (iconElement._tooltipMouseMoveHandler) {
          iconElement.removeEventListener('mousemove', iconElement._tooltipMouseMoveHandler);
          delete iconElement._tooltipMouseMoveHandler;
        }
      };

      iconElement.addEventListener('mouseenter', showTooltip);
      iconElement.addEventListener('mouseleave', hideTooltip);
      iconElement.addEventListener('focus', showTooltip);
      iconElement.addEventListener('blur', hideTooltip);
    });
  }

  /**
   * Attach tooltip event listeners to building upgrade icons
   */
  attachBuildingUpgradesTooltipListeners() {
    const upgradeIcons = this.container.querySelectorAll('.summary-building-upgrades-icon');

    // Create tooltip element if it doesn't exist
    if (!this.tooltipElement) {
      this.tooltipElement = document.createElement('div');
      this.tooltipElement.className = 'route-step-tooltip';
      this.tooltipElement.style.display = 'none';
      document.body.appendChild(this.tooltipElement);
    }

    upgradeIcons.forEach(iconElement => {
      const buildingName = iconElement.getAttribute('data-building-name');
      const upgradesJsonEncoded = iconElement.getAttribute('data-upgrades');
      if (!buildingName || !upgradesJsonEncoded) return;

      let upgrades = [];
      try {
        const upgradesJson = decodeURIComponent(upgradesJsonEncoded);
        upgrades = JSON.parse(upgradesJson);
      } catch (e) {
        console.warn('Failed to parse upgrades JSON:', e);
        return;
      }

      if (upgrades.length === 0) return;

      const showTooltip = (e) => {
        if (!this.tooltipElement) return;
        
        const tooltipContent = this.formatBuildingUpgradesTooltip(buildingName, upgrades);
        this.tooltipElement.innerHTML = tooltipContent;
        this.tooltipElement.style.display = 'block';
        
        // Force reflow to get accurate dimensions
        this.tooltipElement.offsetHeight;
        
        // Position tooltip at mouse location
        const updateTooltipPosition = (mouseEvent) => {
          if (!this.tooltipElement) return;
          
          const tooltipRect = this.tooltipElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
          
          // Get mouse coordinates
          let mouseX = mouseEvent.clientX + scrollLeft;
          let mouseY = mouseEvent.clientY + scrollTop;
          
          // Offset tooltip slightly from cursor
          const offsetX = 15;
          const offsetY = 15;
          
          let left = mouseX + offsetX;
          let top = mouseY + offsetY;
          
          // Adjust if tooltip would go off screen
          if (left + tooltipRect.width > window.innerWidth + scrollLeft - 10) {
            left = mouseX - tooltipRect.width - offsetX;
          }
          if (top + tooltipRect.height > window.innerHeight + scrollTop - 10) {
            top = mouseY - tooltipRect.height - offsetY;
          }
          if (left < scrollLeft + 10) {
            left = scrollLeft + 10;
          }
          if (top < scrollTop + 10) {
            top = scrollTop + 10;
          }
          
          this.tooltipElement.style.top = `${top}px`;
          this.tooltipElement.style.left = `${left}px`;
        };
        
        // Initial position
        updateTooltipPosition(e);
        
        // Update position as mouse moves
        const mouseMoveHandler = (moveEvent) => {
          updateTooltipPosition(moveEvent);
        };
        
        iconElement.addEventListener('mousemove', mouseMoveHandler);
        
        // Store handler for cleanup
        iconElement._tooltipMouseMoveHandler = mouseMoveHandler;
      };

      const hideTooltip = () => {
        if (this.tooltipElement) {
          this.tooltipElement.style.display = 'none';
        }
        // Remove mousemove handler
        if (iconElement._tooltipMouseMoveHandler) {
          iconElement.removeEventListener('mousemove', iconElement._tooltipMouseMoveHandler);
          delete iconElement._tooltipMouseMoveHandler;
        }
      };

      iconElement.addEventListener('mouseenter', showTooltip);
      iconElement.addEventListener('mouseleave', hideTooltip);
      iconElement.addEventListener('focus', showTooltip);
      iconElement.addEventListener('blur', hideTooltip);
    });
  }

  /**
   * Format building upgrades tooltip content
   * @param {string} buildingName - Name of the building
   * @param {Array<string>} upgrades - Array of upgrade names
   * @returns {string} HTML string for tooltip
   */
  formatBuildingUpgradesTooltip(buildingName, upgrades) {
    let html = '<div class="route-summary-tooltip">';
    html += `<div class="summary-section"><strong>${this.escapeHtml(buildingName)} Upgrades:</strong><ul class="summary-list">`;
    for (const upgradeName of upgrades.sort()) {
      html += `<li>${this.escapeHtml(upgradeName)}</li>`;
    }
    html += '</ul></div>';
    html += '</div>';
    return html;
  }

  /**
   * Calculate summary of buildings and upgrades up to a given step
   * @param {number} stepOrder - Step order to calculate summary up to (inclusive)
   * @returns {Object} Summary object with buildings and upgrades
   */
  calculateSummaryUpToStep(stepOrder) {
    if (!this.currentRoute || !this.currentRoute.buildings) {
      return { buildings: {}, upgrades: [] };
    }

    const buildings = { ...(this.currentRoute.startingBuildings || {}) };
    const upgrades = [];

    // Process all steps up to and including stepOrder
    for (const step of this.currentRoute.buildings) {
      if (step.order > stepOrder) {
        break;
      }

      // Check if it's a building (has buildingCount) or upgrade (no buildingCount)
      if (step.buildingCount !== null && step.buildingCount !== undefined) {
        // It's a building
        const buildingName = step.buildingName;
        buildings[buildingName] = (buildings[buildingName] || 0) + 1;
      } else {
        // It's an upgrade
        upgrades.push(step.buildingName);
      }
    }

    return { buildings, upgrades };
  }

  /**
   * Render the permanent route summary section
   * @returns {Promise<string>} HTML string for the summary section
   */
  async renderRouteSummary() {
    if (!this.currentRoute || !this.currentRoute.buildings || this.currentRoute.buildings.length === 0) {
      return '';
    }

    // Calculate final summary (up to the last step)
    const finalStepOrder = this.currentRoute.buildings.length;
    const summary = this.calculateSummaryUpToStep(finalStepOrder);
    
    return await this.formatSummaryDisplay(summary);
  }

  /**
   * Map upgrades to their related buildings based on upgrade effects
   * @param {Array<string>} upgradeNames - Array of upgrade names
   * @param {Object} buildings - Buildings object from summary
   * @param {string} versionId - Version ID to load upgrade data
   * @returns {Promise<Object>} Object with upgradeToBuildings Map and globalUpgrades Array
   */
  async mapUpgradesToBuildings(upgradeNames, buildings, versionId) {
    const upgradeToBuildings = new Map();
    const globalUpgrades = [];
    
    if (!upgradeNames || upgradeNames.length === 0) {
      return { upgradeToBuildings, globalUpgrades };
    }
    
    try {
      const { loadVersionById } = await import('../utils/version-loader.js');
      const version = await loadVersionById(versionId || this.currentVersionId || 'v2052');
      
      if (!version || !version.menu) {
        return { upgradeToBuildings, globalUpgrades };
      }
      
      // Create a map of upgrade names to Upgrade objects
      const upgradeMap = new Map();
      for (const upgrade of version.menu) {
        upgradeMap.set(upgrade.name, upgrade);
      }
      
      // Get list of buildings in the route
      const routeBuildings = Object.keys(buildings).filter(b => buildings[b] > 0);
      
      // For each upgrade in the route, find which buildings it affects
      for (const upgradeName of upgradeNames) {
        const upgrade = upgradeMap.get(upgradeName);
        if (!upgrade || !upgrade.effects) {
          // If we can't find the upgrade definition, add to global upgrades
          globalUpgrades.push(upgradeName);
          continue;
        }
        
        // Get all buildings affected by this upgrade
        const affectedBuildings = new Set();
        let isGlobalUpgrade = false;
        
        for (const target in upgrade.effects) {
          if (target === 'mouse') {
            // Mouse upgrades affect Cursor
            if (routeBuildings.includes('Cursor')) {
              affectedBuildings.add('Cursor');
            }
          } else if (target === 'all') {
            // Global upgrades affect all buildings
            isGlobalUpgrade = true;
            break; // Exit early, this is definitely a global upgrade
          } else if (version.buildingNames && version.buildingNames.includes(target) && routeBuildings.includes(target)) {
            // Direct building target that exists in the route
            affectedBuildings.add(target);
          }
        }
        
        // If it's a global upgrade or affects multiple buildings, add to global section
        if (isGlobalUpgrade || affectedBuildings.size > 1) {
          globalUpgrades.push(upgradeName);
        } else if (affectedBuildings.size === 1) {
          // Upgrade affects exactly one building - add it to that building
          const buildingName = Array.from(affectedBuildings)[0];
          if (!upgradeToBuildings.has(buildingName)) {
            upgradeToBuildings.set(buildingName, []);
          }
          upgradeToBuildings.get(buildingName).push(upgradeName);
        } else {
          // If we can't determine which building, add to global upgrades
          globalUpgrades.push(upgradeName);
        }
      }
    } catch (error) {
      console.warn('Failed to map upgrades to buildings:', error);
      // Fallback: add all upgrades to global upgrades
      globalUpgrades.push(...upgradeNames);
    }
    
    return { upgradeToBuildings, globalUpgrades };
  }

  /**
   * Format summary as HTML for permanent display
   * @param {Object} summary - Summary object with buildings and upgrades
   * @returns {Promise<string>} HTML string for the summary section
   */
  async formatSummaryDisplay(summary) {
    const { buildings, upgrades } = summary;
    
    // Map upgrades to their related buildings
    const { upgradeToBuildings, globalUpgrades } = await this.mapUpgradesToBuildings(upgrades, buildings, this.currentVersionId);
    
    let html = '<div class="route-summary-section">';
    
    // Header with toggle button (collapsible for all routes)
    html += '<div class="route-summary-header">';
    html += '<h3 class="route-summary-title">Route Summary</h3>';
    html += '<button class="route-summary-toggle collapse-toggle" aria-label="Toggle route summary" aria-expanded="false">';
    html += '<span class="collapse-icon">▶</span>';
    html += '</button>';
    html += '</div>';
    
    // Content wrapper with collapsible class - collapsed by default
    html += '<div class="route-summary-content collapsed">';
    
    // Buildings section - sort by game order, with upgrades grouped under each building
    const buildingEntries = Object.entries(buildings)
      .filter(([_, count]) => count > 0);
    const sortedBuildingEntries = this.sortBuildingsByOrder(buildingEntries);
    
    if (sortedBuildingEntries.length > 0) {
      html += '<div class="summary-section-buildings">';
      html += '<h4 class="summary-section-title">Buildings</h4>';
      html += '<div class="summary-buildings-grid">';
      for (const [buildingName, count] of sortedBuildingEntries) {
        html += `<div class="summary-building-item">`;
        html += `<div class="summary-building-header">`;
        html += `<span class="summary-building-name">${this.escapeHtml(buildingName)}</span>`;
        
        // Show upgrades icon if building has upgrades
        const buildingUpgrades = upgradeToBuildings.get(buildingName) || [];
        if (buildingUpgrades.length > 0) {
          const upgradesJson = encodeURIComponent(JSON.stringify(buildingUpgrades.sort()));
          html += `<span class="summary-building-upgrades-icon" data-building-name="${this.escapeHtml(buildingName)}" data-upgrades="${upgradesJson}" aria-label="Show upgrades for ${this.escapeHtml(buildingName)}" tabindex="0">⚡</span>`;
        }
        
        html += `<span class="summary-building-count">${count}</span>`;
        html += `</div>`;
        html += `</div>`;
      }
      html += '</div>';
      html += '</div>';
    }
    
    // Show global upgrades (affect multiple buildings or all buildings)
    if (globalUpgrades.length > 0) {
      html += '<div class="summary-section-upgrades">';
      html += '<h4 class="summary-section-title">Global Upgrades</h4>';
      html += '<div class="summary-upgrades-list">';
      for (const upgradeName of globalUpgrades.sort()) {
        html += `<div class="summary-upgrade-item">${this.escapeHtml(upgradeName)}</div>`;
      }
      html += '</div>';
      html += '</div>';
    }
    
    if (sortedBuildingEntries.length === 0 && upgrades.length === 0) {
      html += '<div class="summary-empty">No buildings or upgrades in this route</div>';
    }
    
    html += '</div>';
    html += '</div>';
    return html;
  }

  /**
   * Format summary as HTML for tooltip
   * @param {Object} summary - Summary object with buildings and upgrades
   * @returns {string} HTML string for tooltip
   */
  formatSummaryTooltip(summary) {
    const { buildings, upgrades } = summary;
    
    let html = '<div class="route-summary-tooltip">';
    
    // Buildings section - sort by game order
    const buildingEntries = Object.entries(buildings)
      .filter(([_, count]) => count > 0);
    const sortedBuildingEntries = this.sortBuildingsByOrder(buildingEntries);
    
    if (sortedBuildingEntries.length > 0) {
      html += '<div class="summary-section"><strong>Buildings:</strong><ul class="summary-list">';
      for (const [buildingName, count] of sortedBuildingEntries) {
        html += `<li>${this.escapeHtml(buildingName)}: ${count}</li>`;
      }
      html += '</ul></div>';
    }
    
    // Upgrades section
    if (upgrades.length > 0) {
      html += '<div class="summary-section"><strong>Upgrades:</strong><ul class="summary-list">';
      for (const upgradeName of upgrades.sort()) {
        html += `<li>${this.escapeHtml(upgradeName)}</li>`;
      }
      html += '</ul></div>';
    }
    
    if (sortedBuildingEntries.length === 0 && upgrades.length === 0) {
      html += '<div class="summary-empty">No buildings or upgrades yet</div>';
    }
    
    html += '</div>';
    return html;
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

